import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import "./Settings.scss";

import archiveIcon from "./img/archive.png";
import statisticsIcon from "./img/statistics.png";
import billingIcon from "./img/billing.png";
import modeIcon from "./img/mode.png";
import securityIcon from "./img/sequirity.png";
import languageIcon from "./img/language.png";
import blockIcon from "./img/block.png";
import supportIcon from "./img/support.png";
import rulesIcon from "./img/rules.png";
import privacyIcon from "./img/privacy.png";
import logoutIcon from "./img/logout.png";
import subscriptionIcon from "./img/subscription.png";
import loyaltyIcon from "./img/loyalty.png";
import moderationIcon from "./img/moderation.png";
import adsIcon from "./img/ads.png";
import trustIcon from "./img/trust.png";
import promotionIcon from "./img/promotion.png";
import sunImg from "./img/sun.png";
import moonImg from "./img/moon.png";
import ukrImg from "./img/ukr.png";
import engImg from "./img/eng.png";

import { getAccessToken } from "../../awsAuth";
import DynamicHeader from "../Header/DynamicHeader";
import { useTheme } from "../../contexts/ThemeContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { useWallet } from "../../contexts/WalletContext";

const PLAN_LABELS = {
  free: "Cvanto Free",
  plus: "Cvanto Plus",
  business: "Cvanto Business",
};

const API =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API ||
  (typeof window !== "undefined" &&
  window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

// Options
const baseOptions = [
  {
    icon: archiveIcon,
    label: "Архів",
    route: "/settings/archive",
    group: "account",
    iconClass: "icon-archive",
  },
  // {
    // icon: promotionIcon,
    // label: "Просування та підняття",
    // route: "/settings/promotion",
    // group: "account",
    // iconClass: "icon-promo",
  // },
  // {
    // icon: billingIcon,
    // label: "Платежі",
    // route: "/settings/billing",
    // group: "account",
    // iconClass: "icon-payments",
  // },
  {
    icon: billingIcon,
    label: "Баланс",
    route: "/settings/balance",
    group: "account",
  },
  {
    icon: subscriptionIcon,
    label: "Ваш тариф",
    route: "/settings/subscription",
    group: "account",
    iconClass: "icon-plan",
  },
  {
    icon: statisticsIcon,
    label: "Статистика",
    route: "/settings/statistics",
    group: "account",
  },
  {
    // Cvanto Search Phase 4B — reuses the archive icon (closest existing
    // visual fit for "a list of your own past items") rather than adding a
    // new image asset for one lightweight MVP entry.
    icon: archiveIcon,
    label: "Мої запити",
    route: "/settings/buyer-requests",
    group: "account",
  },
  {
    // §41 — private sizes/style/preferences for personal shopping; same data as the mobile app
    icon: loyaltyIcon,
    label: "Shopping Profile",
    route: "/settings/shopping-profile",
    group: "personalization",
  },
  {
    icon: modeIcon,
    label: "Колір інтерфейсу",
    action: "theme",
    group: "personalization",
  },
  {
    icon: securityIcon,
    label: "Акаунт та безпека",
    route: "/settings/security",
    group: "account",
  },
  {
    icon: trustIcon,
    label: "Рейтинг і довіра",
    route: "/settings/trust",
    group: "account",
    iconClass: "icon-rating",
  },
  {
    icon: languageIcon,
    label: "Мова",
    action: "language",
    group: "personalization",
  },
  {
    icon: blockIcon,
    label: "Заблоковані",
    route: "/settings/blocked",
    group: "personalization",
    iconClass: "icon-blocked",
  },
  {
    icon: supportIcon,
    label: "Допомога та технічна підтримка",
    route: "/settings/help",
    group: "help",
    iconClass: "icon-help",
  },
  {
    icon: supportIcon,
    label: "Про Cvanto",
    route: "/about",
    group: "help",
  },
  {
    icon: rulesIcon,
    label: "Правила спільноти",
    route: "/settings/terms",
    group: "help",
  },
  {
    icon: privacyIcon,
    label: "Політика конфіденційності",
    route: "/settings/privacy",
    group: "help",
  },
  // {
    // icon: loyaltyIcon,
    // label: "Підтримати Cvanto",
    // route: "/settings/support-cvanto",
    // group: "help",
    // iconClass: "icon-support",
  // },
];

const Settings = () => {
  const navigate = useNavigate();
  const { setIsLoggedIn } = useAuth();
  const { theme: ctxTheme, setTheme: setGlobalTheme } = useTheme() || {};
  const { language: ctxLanguage, setLanguage: setGlobalLanguage } = useLanguage() || {};
  const { plan: currentPlan, loading: subscriptionLoading, error: subscriptionError } = useSubscription();
  // Don't show a plan label at all until the real one is known — otherwise
  // this would flash the default ("free") state for every user on mount,
  // regardless of their actual plan, until GET /api/subscription/me resolves.
  // On error, `plan` falls back to "free" internally (see SubscriptionContext)
  // but that's never a confirmed value — never render it as if it were.
  const currentPlanLabel =
    subscriptionLoading || subscriptionError
      ? ""
      : PLAN_LABELS[currentPlan] || PLAN_LABELS.free;

  const { balance: walletBalance, loading: walletLoading, error: walletError } = useWallet();
  const balanceLabel = walletLoading || walletError ? "" : `${walletBalance} Tokens`;

  const [theme, setTheme] = useState(
  ctxTheme === "dark" || ctxTheme === "light" ? ctxTheme : "dark"
  );
  const [savingTheme, setSavingTheme] = useState(false);

  const [language, setLanguage] = useState(
  ctxLanguage === "en" || ctxLanguage === "uk" ? ctxLanguage : "uk");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [pageState, setPageState] = useState("enter");

  // sync with ThemeContext
  useEffect(() => {
    if (ctxTheme === "dark" || ctxTheme === "light") {
      setTheme(ctxTheme);
    }
  }, [ctxTheme]);

  // sync with LanguageContext
useEffect(() => {
  if (ctxLanguage === "en" || ctxLanguage === "uk") {
    setLanguage(ctxLanguage);
  }
}, [ctxLanguage]);

  // Staff check
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = await getAccessToken();
        if (!token) {
          if (alive) setIsStaff(false);
          return;
        }
        const r = await fetch(`${API}/api/admin/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const j = await r.json().catch(() => ({}));
        const ok = !!(j?.data?.isAdmin || j?.data?.isMod);
        if (alive) setIsStaff(ok);
      } catch {
        if (alive) setIsStaff(false);
      }
    })();
    const t = setTimeout(() => setPageState("idle"), 180);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("authToken");
    setIsLoggedIn(false);
    navigate("/auth");
  }, [navigate, setIsLoggedIn]);

  // theme via ThemeContext
  const persistTheme = useCallback(
    async (newTheme) => {
      if (setGlobalTheme) {
        setSavingTheme(true);
        try {
          await Promise.resolve(setGlobalTheme(newTheme));
        } finally {
          setSavingTheme(false);
        }
      } else {
        setTheme(newTheme);
      }
    },
    [setGlobalTheme]
  );

  const onToggleTheme = useCallback(() => {
    const next = theme === "light" ? "dark" : "light";
    setTheme(next);
    persistTheme(next);
  }, [theme, persistTheme]);

  const toggleTheme = (newTheme) => {
    setTheme(newTheme);
    persistTheme(newTheme);
  };

  // Language toggle
  const onToggleLanguage = useCallback(() => {
  const next = language === "uk" ? "en" : "uk";
  setLanguage(next);

  if (setGlobalLanguage) {
    setGlobalLanguage(next);
  } else {
    // fallback якщо контекст чомусь не підключений
    localStorage.setItem("cvanto_lang", next);
    if (typeof window.cvantoSetLang === "function") window.cvantoSetLang(next);
    window.dispatchEvent(new CustomEvent("cvanto:set-lang", { detail: { lang: next } }));
  }
}, [language, setGlobalLanguage]);


  // Sections
  const optionsWithStaff = useMemo(() => {
  if (isStaff) {
  return [
    // {
    //   icon: moderationIcon,
    //   label: "Модерація",
    //   route: "/settings/admin-reports",
    //   group: "account",
    // },

    //{
    // icon: adsIcon,
    //  label: "Кабінет реклами",
    //  route: "/settings/ads-cabinet",
    //  group: "account",
    //},

    ...baseOptions,
  ];
}
  return baseOptions;
}, [isStaff]);


  const sections = useMemo(() => {
    const acc = optionsWithStaff.filter((o) => o.group === "account");
    const per = optionsWithStaff.filter((o) => o.group === "personalization");
    const help = optionsWithStaff.filter((o) => o.group === "help");
    return [
      { title: "ОБЛІКОВИЙ ЗАПИС", items: acc },
      { title: "ПЕРСОНАЛІЗАЦІЯ", items: per },
      { title: "ДОПОМОГА", items: help },
    ];
  }, [optionsWithStaff]);

  const handleOptionClick = (option) => {
    if (option.action === "theme" || option.action === "language") {
      return;
    }
    if (option.route) {
      setPageState("leave");
      setTimeout(() => navigate(option.route), 160);
    }
  };

  return (
    <>
      <DynamicHeader
        title="Налаштування"
        variant="glass"
        size="regular"
        hideOnScroll={false}
        showBack={true}
        showMenu={false}
        withSpacer={false}
      />
      <div className="local-header-spacer" />

      <div className={`settings page-${pageState}`}>
        {sections.map((section) => (
          <div key={section.title} className="settings-section">
            <div className="section-title">{section.title}</div>
            <div className="section-card">
              {section.items.map((option) => {
                if (option.action === "theme") {
                  return (
                    <div
                      key={option.label}
                      className="settings-item is-toggle"
                      aria-label="Налаштування теми"
                    >
                      <span className="left">
                        <img
                          src={option.icon}
                          alt=""
                          className={`settings-icon ${
                            option.iconClass || ""
                          }`}
                        />
                        <span className="label">{option.label}</span>
                      </span>

                      <button
                        type="button"
                        className={`theme-toggle ${theme} ${
                          savingTheme ? "is-saving" : ""
                        }`}
                        onClick={onToggleTheme}
                        aria-pressed={theme === "dark"}
                        aria-label={
                          theme === "dark"
                            ? "Увімкнена темна тема"
                            : "Увімкнена світла тема"
                        }
                      >
                        <span className="thumb">
                          <img
                            src={theme === "dark" ? moonImg : sunImg}
                            alt={
                              theme === "dark"
                                ? "Темна тема"
                                : "Світла тема"
                            }
                            draggable="false"
                          />
                        </span>
                        <span className="rail" />
                      </button>
                    </div>
                  );
                }

                if (option.action === "language") {
                  return (
                    <div
                      key={option.label}
                      className="settings-item is-toggle"
                      aria-label="Налаштування мови"
                    >
                      <span className="left">
                        <img
                          src={option.icon}
                          alt=""
                          className={`settings-icon ${
                            option.iconClass || ""
                          }`}
                        />
                        <span className="label">{option.label}</span>
                      </span>

                      <button
                        type="button"
                        className={`lang-toggle ${
                          language === "en" ? "en" : "uk"
                        }`}
                        onClick={onToggleLanguage}
                        aria-pressed={language === "en"}
                        aria-label={
                          language === "en"
                            ? "English увімкнено"
                            : "Українська увімкнено"
                        }
                      >
                        <span className="thumb">
                          <img
                            src={language === "en" ? engImg : ukrImg}
                            alt={language === "en" ? "English" : "Українська"}
                            draggable="false"
                          />
                        </span>
                        <span className="rail" />
                      </button>
                    </div>
                  );
                }

                const isSubscriptionItem = option.route === "/settings/subscription";
                const isBalanceItem = option.route === "/settings/balance";

                return (
                  <button
                    key={option.label}
                    className="settings-item"
                    onClick={() => handleOptionClick(option)}
                  >
                    <span className="left">
                      <img
                        src={option.icon}
                        alt=""
                        className={`settings-icon ${
                          option.iconClass || ""
                        }`}
                      />
                      <span className="label">{option.label}</span>
                    </span>
                    <span className="settings-item-right">
                      {isSubscriptionItem && subscriptionLoading && (
                        <span
                          className="settings-item-value settings-item-value--skeleton"
                          aria-hidden="true"
                        />
                      )}
                      {isSubscriptionItem && !subscriptionLoading && subscriptionError && (
                        <span className="settings-item-value settings-item-value--error">
                          Помилка завантаження
                        </span>
                      )}
                      {isSubscriptionItem && !subscriptionLoading && !subscriptionError && currentPlanLabel && (
                        <span className="settings-item-value">
                          {currentPlanLabel}
                        </span>
                      )}
                      {isBalanceItem && walletLoading && (
                        <span
                          className="settings-item-value settings-item-value--skeleton"
                          aria-hidden="true"
                        />
                      )}
                      {isBalanceItem && !walletLoading && walletError && (
                        <span className="settings-item-value settings-item-value--error">
                          Помилка завантаження
                        </span>
                      )}
                      {isBalanceItem && !walletLoading && !walletError && balanceLabel && (
                        <span className="settings-item-value">
                          {balanceLabel}
                        </span>
                      )}
                      <span className="chevron" aria-hidden="true">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M9 6l6 6-6 6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <div className="settings-section settings-logout-section">
          <div className="section-card danger is-single">
            <button
              className="settings-item is-logout"
              onClick={handleLogout}
            >
              <span className="left">
                <img src={logoutIcon} alt="" className="settings-icon" />
                <span className="label">Вийти</span>
              </span>
              <span className="chevron" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 6l6 6-6 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="modal" onClick={() => setIsModalOpen(false)}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Оберіть тему</h3>
            <div className="theme-actions">
              <button
                onClick={() => toggleTheme("light")}
                className={theme === "light" ? "active" : ""}
              >
                Світла
              </button>
              <button
                onClick={() => toggleTheme("dark")}
                className={theme === "dark" ? "active" : ""}
              >
                Темна
              </button>
              <button
                onClick={() => setIsModalOpen(false)}
                className="primary"
              >
                Зберегти
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Settings;
